'use client';

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';

interface VideoElementProps {
  src: string;
  className?: string;
  scroller: string;
}

const VideoElement = ({ src, className, scroller }: VideoElementProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);

  const markReady = () => setIsReady(true);

  useEffect(() => {
    setIsReady(false);
    setHasError(false);
    const video = videoRef.current;
    if (video && video.readyState >= 2) {
      setIsReady(true);
    }
  }, [src]);

  useGSAP(
    () => {
      if (!isReady || !videoRef.current) {
        return;
      }

      // Recover from autoplay being blocked under CPU/network throttling.
      videoRef.current.play().catch(() => {});

      gsap.fromTo(
        videoRef.current,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: videoRef.current,
            start: 'top 90%',
            scroller: scroller,
          },
        }
      );
    },
    { dependencies: [isReady, src, scroller], revertOnUpdate: true }
  );

  if (hasError) {
    return (
      <div
        className={className}
        role="img"
        aria-label="Project media failed to load"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 1,
          opacity: 0.6,
        }}
      >
        Media unavailable
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      className={className}
      autoPlay
      loop
      muted
      playsInline
      preload="metadata"
      src={src}
      aria-label="Project media video"
      onLoadedData={markReady}
      onCanPlay={markReady}
      onError={() => setHasError(true)}
      style={{ opacity: isReady ? undefined : 0 }}
    >
      <track
        kind="captions"
        src="/captions/no-audio.vtt"
        srcLang="en"
        label="No spoken audio"
      />
    </video>
  );
};

export default VideoElement;
